import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ChevronLeft, Loader2, Save, Trash2, AlertTriangle,
  User, Building2, Users, Lock, Camera, ImagePlus, Mail, KeyRound, X,
  Share2, Copy, Eye, FileDown, Clock as ClockIcon, Link2,
  Heart, Plus, ExternalLink, Pencil,
} from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { PasswordInput } from "../components/ui/password-input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const MAX_BYTES = 2 * 1024 * 1024;

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function ImagePicker({ value, onChange, label, hint, testid, rounded = false }) {
  const handle = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Image must be under 2 MB");
      return;
    }
    onChange(await readFileAsDataURL(file));
  };
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-slate-800">{label}</Label>
      <label
        className={`flex items-center gap-4 p-4 border-2 border-dashed border-slate-300 hover:border-emerald-400 transition-colors cursor-pointer bg-slate-50/60 ${rounded ? "rounded-md" : "rounded-md"}`}
        data-testid={testid}
      >
        {value ? (
          <img
            src={value}
            alt={label}
            className={`object-cover bg-white ring-1 ring-slate-200 ${rounded ? "h-16 w-16 rounded-full" : "h-16 w-24 rounded-sm"}`}
          />
        ) : (
          <div className={`grid place-items-center bg-white ring-1 ring-slate-200 ${rounded ? "h-16 w-16 rounded-full" : "h-16 w-24 rounded-sm"}`}>
            {rounded ? <Camera className="w-6 h-6 text-slate-400" /> : <ImagePlus className="w-6 h-6 text-slate-400" />}
          </div>
        )}
        <div className="flex-1">
          <div className="text-sm font-medium text-slate-800">{value ? "Replace" : "Choose"} a file</div>
          <div className="text-xs text-slate-500 mt-0.5">{hint || "PNG, JPG, or SVG · up to 2 MB"}</div>
        </div>
        <input type="file" accept="image/*" className="hidden" onChange={(e) => handle(e.target.files?.[0])} data-testid={`${testid}-input`} />
        {value && (
          <button
            type="button"
            className="text-rose-600 hover:text-rose-700 text-xs flex items-center gap-1 px-2 py-1"
            onClick={(e) => { e.preventDefault(); onChange(null); }}
            data-testid={`${testid}-clear`}
          >
            <Trash2 className="w-3.5 h-3.5" /> Remove
          </button>
        )}
      </label>
    </div>
  );
}

// ============================================================
// Profile tab
// ============================================================
function ProfileTab({ data, onSaved }) {
  const [form, setForm] = useState({
    full_name: data.full_name || data.name || "",
    designation: data.designation || "",
    profile_picture: data.profile_picture || null,
  });
  const [saving, setSaving] = useState(false);

  // Password change box (separate submit)
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwdSaving, setPwdSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        full_name: form.full_name,
        designation: form.designation,
      };
      if (form.profile_picture === null) patch.clear_profile_picture = true;
      else if (form.profile_picture && form.profile_picture !== data.profile_picture) patch.profile_picture = form.profile_picture;
      const { data: fresh } = await api.patch("/users/me/profile", patch);
      toast.success("Profile updated");
      onSaved?.(fresh);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save profile");
    } finally {
      setSaving(false);
    }
  };

  const submitPassword = async () => {
    if (pwd.new_password.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (pwd.new_password !== pwd.confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setPwdSaving(true);
    try {
      await api.post("/users/me/password", { current_password: pwd.current_password, new_password: pwd.new_password });
      setPwd({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't change password");
    } finally {
      setPwdSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-profile-tab">
      <section className="space-y-4">
        <h3 className="font-heading text-lg font-semibold text-slate-900">Profile</h3>
        <ImagePicker
          label="Profile picture"
          hint="Square image works best · up to 2 MB"
          value={form.profile_picture}
          onChange={(v) => setForm((f) => ({ ...f, profile_picture: v }))}
          testid="settings-profile-picture"
          rounded
        />
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="full-name">Full name</Label>
            <Input
              id="full-name"
              value={form.full_name}
              onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
              data-testid="settings-full-name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="designation">Role / Designation</Label>
            <Input
              id="designation"
              value={form.designation}
              onChange={(e) => setForm((f) => ({ ...f, designation: e.target.value }))}
              placeholder="e.g. Director, M&E"
              data-testid="settings-designation"
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="email-readonly">Email</Label>
            <Input
              id="email-readonly"
              value={data.email}
              disabled
              className="bg-slate-100"
              data-testid="settings-email-readonly"
            />
            <p className="text-xs text-slate-400">To change your email, use the Account tab.</p>
          </div>
        </div>
        <div className="flex justify-end pt-2">
          <Button
            onClick={save}
            disabled={saving}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="settings-profile-save"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save changes
          </Button>
        </div>
      </section>

      <section className="space-y-4 border-t border-slate-200 pt-6">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">Change password</h3>
          <p className="text-sm text-slate-500 mt-1">Use 8 characters or more. Mix letters, numbers, and symbols.</p>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="cur-pwd">Current password</Label>
            <PasswordInput
              id="cur-pwd"
              value={pwd.current_password}
              onChange={(e) => setPwd((p) => ({ ...p, current_password: e.target.value }))}
              testid="settings-current-password"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-pwd">New password</Label>
            <PasswordInput
              id="new-pwd"
              value={pwd.new_password}
              onChange={(e) => setPwd((p) => ({ ...p, new_password: e.target.value }))}
              testid="settings-new-password"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-pwd">Confirm new password</Label>
            <PasswordInput
              id="confirm-pwd"
              value={pwd.confirm}
              onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))}
              testid="settings-confirm-password"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            onClick={submitPassword}
            disabled={pwdSaving || !pwd.current_password || !pwd.new_password}
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-900"
            data-testid="settings-update-password"
          >
            {pwdSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
            Update password
          </Button>
        </div>
      </section>
    </div>
  );
}

// ============================================================
// Organization tab — branding centre lives here
// ============================================================
function OrganizationTab({ data, industries, onSaved }) {
  const [form, setForm] = useState({
    org_name: data.org_name || "",
    industry: data.industry || "",
    location: data.location || "",
    departments: (data.departments || []).join(", "),
    org_logo: data.org_logo || null,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        org_name: form.org_name,
        industry: form.industry,
        location: form.location,
        departments: form.departments.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (form.org_logo === null) patch.clear_org_logo = true;
      else if (form.org_logo && form.org_logo !== data.org_logo) patch.org_logo = form.org_logo;
      const { data: fresh } = await api.patch("/users/me/organization", patch);
      toast.success("Organization updated");
      onSaved?.(fresh);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save organization");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-org-tab">
      <section>
        <h3 className="font-heading text-lg font-semibold text-slate-900">Organization information</h3>
        <p className="text-sm text-slate-500 mt-1">
          Donors and partners see this on every report you publish. The logo replaces Array Engine branding on Starter and higher plans.
        </p>
      </section>
      <ImagePicker
        label="Organization logo"
        hint="Wide or square logo · used on reports & PDF · up to 2 MB"
        value={form.org_logo}
        onChange={(v) => setForm((f) => ({ ...f, org_logo: v }))}
        testid="settings-org-logo"
      />
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="org-name">Organization name</Label>
          <Input
            id="org-name"
            value={form.org_name}
            onChange={(e) => setForm((f) => ({ ...f, org_name: e.target.value }))}
            data-testid="settings-org-name"
          />
        </div>
        <div className="space-y-2">
          <Label>Sector / Focus area</Label>
          <Select value={form.industry} onValueChange={(v) => setForm((f) => ({ ...f, industry: v }))}>
            <SelectTrigger data-testid="settings-industry-trigger">
              <SelectValue placeholder="Choose your sector" />
            </SelectTrigger>
            <SelectContent>
              {industries.map((i) => (
                <SelectItem key={i} value={i}>{i}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="org-location">Location</Label>
          <Input
            id="org-location"
            value={form.location}
            onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
            placeholder="e.g. Lagos, Nigeria"
            data-testid="settings-org-location"
          />
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="org-departments">Departments</Label>
          <Textarea
            id="org-departments"
            rows={2}
            value={form.departments}
            onChange={(e) => setForm((f) => ({ ...f, departments: e.target.value }))}
            placeholder="e.g. Programmes, Finance, M&E (comma separated)"
            data-testid="settings-org-departments"
          />
          <p className="text-[11px] text-slate-400">Separate with commas.</p>
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-slate-900 hover:bg-slate-800 text-white"
          data-testid="settings-org-save"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save changes
        </Button>
      </div>

      <OrgBrandColorSection />
    </div>
  );
}

// Phase 2c — wire the v2 org_profile primary_color to a small subsection
// so the dashboard chip + future widgets pick up the user's brand.
function OrgBrandColorSection() {
  const [profile, setProfile] = useState(null);
  const [color, setColor] = useState("#10b981");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/dashboard/v2/org-profile")
      .then(({ data }) => { setProfile(data); setColor(data.primary_color || "#10b981"); })
      .catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/dashboard/v2/org-profile", { primary_color: color });
      setProfile(data);
      toast.success("Brand color saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="border-t border-slate-100 pt-6" data-testid="settings-org-brand-color">
      <h3 className="font-heading text-lg font-semibold text-slate-900">Dashboard brand color</h3>
      <p className="text-sm text-slate-500 mt-1">
        Used in the dashboard org chip and the upper-deck AI Insights card when no logo image is set.
      </p>
      <div className="mt-4 flex items-end gap-3">
        <div className="flex-1 space-y-2 max-w-xs">
          <Label htmlFor="org-primary-color">Primary color (hex)</Label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-10 h-10 rounded-sm border border-slate-200 bg-white cursor-pointer"
              data-testid="settings-org-color-picker"
            />
            <Input
              id="org-primary-color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              maxLength={7}
              data-testid="settings-org-color-input"
            />
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          Preview
          <span
            className="w-6 h-6 rounded-sm border border-slate-200 grid place-items-center text-white text-[10px] font-semibold"
            style={{ background: color }}
            data-testid="settings-org-color-preview"
          >
            {(profile?.name || "?").slice(0, 1).toUpperCase()}
          </span>
        </div>
        <Button onClick={save} disabled={saving || !profile} className="bg-slate-900 hover:bg-slate-800 text-white ml-auto" data-testid="settings-org-color-save">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save color
        </Button>
      </div>
    </section>
  );
}

// ============================================================
// Team management
// ============================================================
function TeamTab() {
  const [teams, setTeams] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/users/me/team").then(({ data }) => {
      if (alive) setTeams(data);
    }).catch(() => alive && setTeams([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  if (loading) {
    return <div className="grid place-items-center py-12 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }
  if (!teams || teams.length === 0) {
    return (
      <div className="border border-dashed border-slate-200 rounded-md p-10 text-center" data-testid="settings-team-empty">
        <Users className="w-8 h-8 text-slate-300 mx-auto" />
        <p className="text-sm font-medium text-slate-700 mt-3">No teams yet</p>
        <p className="text-xs text-slate-500 mt-1">Create a program first to invite members.</p>
        <Link to="/" className="text-xs text-emerald-700 underline mt-3 inline-block">Go to programs</Link>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="settings-team-tab">
      <div>
        <h3 className="font-heading text-lg font-semibold text-slate-900">Team management</h3>
        <p className="text-sm text-slate-500 mt-1">
          Add, remove and assign roles for every program you own. Click a program to manage its members and pending invites.
        </p>
      </div>
      <div className="space-y-3">
        {teams.map((p) => (
          <div key={p.id} className="border border-slate-200 rounded-md p-4 hover:border-slate-300 transition-colors" data-testid={`settings-team-program-${p.id}`}>
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-medium text-slate-900">{p.name}</p>
                  <span className="text-[10px] uppercase tracking-widest bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-sm">
                    {p.members.length + 1} member{p.members.length === 0 ? "" : "s"}
                  </span>
                  {p.pending_invites.length > 0 && (
                    <span className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-sm">
                      {p.pending_invites.length} pending
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  <span className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-sm">
                    Owner: {p.owner.name}
                  </span>
                  {p.members.slice(0, 6).map((m) => (
                    <span key={m.user_id} className="text-xs bg-slate-50 text-slate-700 border border-slate-200 px-2 py-0.5 rounded-sm">
                      {m.name || m.email} · {m.role}
                    </span>
                  ))}
                  {p.members.length > 6 && (
                    <span className="text-xs text-slate-500 px-2 py-0.5">+ {p.members.length - 6} more</span>
                  )}
                </div>
              </div>
              <Link
                to={`/programs/${p.id}?tab=members`}
                className="shrink-0 text-xs font-medium text-slate-900 underline underline-offset-2 hover:text-emerald-700"
                data-testid={`settings-team-manage-${p.id}`}
              >
                Manage →
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// Account tab — change email + delete account
// ============================================================
function AccountTab({ data }) {
  const navigate = useNavigate();
  const { logout, refreshUser } = useAuth();
  const [emailForm, setEmailForm] = useState({ new_email: "", current_password: "" });
  const [emailSaving, setEmailSaving] = useState(false);

  const [delForm, setDelForm] = useState({ current_password: "", confirm: "" });
  const [delOpen, setDelOpen] = useState(false);
  const [delBusy, setDelBusy] = useState(false);

  const submitEmail = async () => {
    if (!emailForm.new_email || !emailForm.current_password) {
      toast.error("Both fields are required");
      return;
    }
    setEmailSaving(true);
    try {
      await api.post("/users/me/email", emailForm);
      toast.success("Email updated. Use the new address to sign in next time.");
      setEmailForm({ new_email: "", current_password: "" });
      await refreshUser();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't change email");
    } finally {
      setEmailSaving(false);
    }
  };

  const submitDelete = async () => {
    setDelBusy(true);
    try {
      await api.delete("/users/me", { data: delForm });
      toast.success("Account deleted. We're sorry to see you go.");
      await logout();
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't delete account");
    } finally {
      setDelBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-account-tab">
      <section className="space-y-4">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">Change email address</h3>
          <p className="text-sm text-slate-500 mt-1">
            Currently signed in as <span className="font-medium text-slate-700">{data.email}</span>.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="new-email">New email</Label>
            <Input
              id="new-email"
              type="email"
              value={emailForm.new_email}
              onChange={(e) => setEmailForm((f) => ({ ...f, new_email: e.target.value }))}
              placeholder="john.doe@email.com"
              data-testid="settings-new-email"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email-pwd">Current password</Label>
            <PasswordInput
              id="email-pwd"
              value={emailForm.current_password}
              onChange={(e) => setEmailForm((f) => ({ ...f, current_password: e.target.value }))}
              testid="settings-email-current-password"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            onClick={submitEmail}
            disabled={emailSaving}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="settings-save-email"
          >
            {emailSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
            Update email
          </Button>
        </div>
      </section>

      <section className="space-y-4 border-t border-rose-200 pt-6">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-sm bg-rose-50 grid place-items-center">
            <AlertTriangle className="w-5 h-5 text-rose-500" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-semibold text-rose-900">Delete account</h3>
            <p className="text-sm text-rose-700/80 mt-1 max-w-md">
              Permanently delete your account. Programs you own will be unassigned, but data is retained for audit.
              This action cannot be undone.
            </p>
          </div>
        </div>
        {!delOpen ? (
          <Button
            onClick={() => setDelOpen(true)}
            variant="outline"
            className="border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
            data-testid="settings-delete-account-open"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete my account
          </Button>
        ) : (
          <div className="border border-rose-200 bg-rose-50/40 rounded-md p-4 space-y-4" data-testid="settings-delete-account-confirm">
            <div className="space-y-2">
              <Label>Current password</Label>
              <PasswordInput
                value={delForm.current_password}
                onChange={(e) => setDelForm((f) => ({ ...f, current_password: e.target.value }))}
                testid="settings-delete-password"
              />
            </div>
            <div className="space-y-2">
              <Label>Type <span className="font-mono text-rose-700">DELETE</span> to confirm</Label>
              <Input
                value={delForm.confirm}
                onChange={(e) => setDelForm((f) => ({ ...f, confirm: e.target.value }))}
                placeholder="DELETE"
                data-testid="settings-delete-confirm"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={submitDelete}
                disabled={delBusy || delForm.confirm !== "DELETE"}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="settings-delete-account-submit"
              >
                {delBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Permanently delete
              </Button>
              <Button
                variant="ghost"
                onClick={() => { setDelOpen(false); setDelForm({ current_password: "", confirm: "" }); }}
                data-testid="settings-delete-account-cancel"
              >
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

// ============================================================
// Share links tab — workspace-wide governance
// ============================================================
function relativeTime(iso) {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

function ShareLinksTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgram, setBulkProgram] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/share-links");
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't load share links"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const copy = async (token) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/share/${token}`);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  const revoke = async (pid, token, label) => {
    if (!window.confirm(`Revoke "${label || "Untitled link"}"? Anyone with the URL will lose access immediately.`)) return;
    try {
      await api.delete(`/programs/${pid}/shares/${token}`);
      toast.success("Link revoked");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't revoke"));
    }
  };

  const bulkRevoke = async (scope, programId) => {
    const confirmCopy = scope === "expired"
      ? `Revoke all ${expiredCount} expired link${expiredCount === 1 ? "" : "s"}? This frees the dashboard but cannot be undone.`
      : `Revoke every active link on "${programs.find((p) => p.id === programId)?.name || "this program"}"? All donors with these URLs will lose access.`;
    if (!window.confirm(confirmCopy)) return;
    setBulkBusy(true);
    try {
      const body = scope === "expired" ? { scope } : { scope: "program", program_id: programId };
      const { data } = await api.post("/me/share-links/bulk-revoke", body);
      toast.success(`Revoked ${data.revoked_count} link${data.revoked_count === 1 ? "" : "s"}`);
      setBulkProgram("");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err, "Bulk revoke failed"));
    } finally {
      setBulkBusy(false);
    }
  };

  const filtered = items.filter((it) => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (
      (it.program_name || "").toLowerCase().includes(f) ||
      (it.label || "").toLowerCase().includes(f)
    );
  });

  const totals = items.reduce(
    (acc, it) => ({
      views: acc.views + (it.views || 0),
      uniques: acc.uniques + (it.unique_visitors || 0),
      pdfs: acc.pdfs + (it.pdf_downloads || 0),
    }),
    { views: 0, uniques: 0, pdfs: 0 },
  );

  // Distinct programs in the result set + expired-count are computed once
  // for the bulk-action affordances.
  const programs = Array.from(
    items.reduce((m, it) => {
      if (!m.has(it.program_id)) m.set(it.program_id, { id: it.program_id, name: it.program_name });
      return m;
    }, new Map()).values(),
  );
  const expiredCount = items.filter((it) => it.status === "expired").length;

  return (
    <div data-testid="share-links-tab">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
            <Share2 className="w-5 h-5 text-emerald-600" /> Public share links
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-xl">
            Every active link across every program you can admin. Audit who has access to your data, see live engagement, and revoke instantly.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="bg-slate-900 text-white px-2.5 py-1 rounded-sm font-mono" data-testid="share-links-total">
            {items.length} active
          </span>
          <span className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-2.5 py-1 rounded-sm font-mono">
            {totals.views} views
          </span>
          <span className="bg-blue-50 border border-blue-200 text-blue-800 px-2.5 py-1 rounded-sm font-mono">
            {totals.uniques} unique
          </span>
          <span className="bg-amber-50 border border-amber-200 text-amber-800 px-2.5 py-1 rounded-sm font-mono">
            {totals.pdfs} PDFs
          </span>
        </div>
      </header>

      {items.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3" data-testid="share-links-toolbar">
          <Input
            placeholder="Filter by program or label…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="text-sm max-w-sm"
            data-testid="share-links-filter"
          />
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={() => bulkRevoke("expired")}
              disabled={bulkBusy || expiredCount === 0}
              className="text-amber-700 border-amber-200 hover:bg-amber-50 disabled:opacity-50"
              data-testid="bulk-revoke-expired"
              title={expiredCount === 0 ? "No expired links to revoke" : `Revoke ${expiredCount} expired link(s)`}
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
              Revoke {expiredCount} expired
            </Button>
            <div className="flex items-center gap-1.5">
              <Select value={bulkProgram} onValueChange={setBulkProgram}>
                <SelectTrigger className="text-sm w-[200px]" data-testid="bulk-program-select">
                  <SelectValue placeholder="Revoke all on program…" />
                </SelectTrigger>
                <SelectContent>
                  {programs.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`bulk-program-option-${p.id}`}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                onClick={() => bulkRevoke("program", bulkProgram)}
                disabled={bulkBusy || !bulkProgram}
                className="text-rose-600 hover:text-rose-700 border-rose-200 disabled:opacity-50"
                data-testid="bulk-revoke-program"
                title={!bulkProgram ? "Pick a program first" : "Revoke every active link on this program"}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-slate-400 py-6" data-testid="share-links-loading">Loading…</p>
      ) : items.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-slate-200 rounded-md" data-testid="share-links-empty">
          <Link2 className="w-8 h-8 mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">No public share links yet.</p>
          <p className="text-xs text-slate-400 mt-1">Open any program → Report → Share to create one.</p>
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-slate-400 py-6" data-testid="share-links-no-match">No links match "{filter}".</p>
      ) : (
        <div className="overflow-x-auto -mx-2 sm:mx-0">
          <table className="w-full text-sm" data-testid="share-links-table">
            <thead>
              <tr className="text-left border-b border-slate-200">
                <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wider text-slate-500">Program</th>
                <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wider text-slate-500">Label</th>
                <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wider text-slate-500">Status</th>
                <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wider text-slate-500 text-right">Views</th>
                <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wider text-slate-500 text-right">Unique</th>
                <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wider text-slate-500 text-right">PDFs</th>
                <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wider text-slate-500">Last opened</th>
                <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wider text-slate-500 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => {
                const status = it.status === "active"
                  ? { cls: "bg-emerald-50 border-emerald-200 text-emerald-700", label: "Active" }
                  : it.status === "expired"
                    ? { cls: "bg-amber-50 border-amber-200 text-amber-700", label: "Expired" }
                    : { cls: "bg-rose-50 border-rose-200 text-rose-700", label: "Revoked" };
                return (
                  <tr key={it.token} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`share-row-${it.token}`}>
                    <td className="px-3 py-3">
                      <Link to={`/programs/${it.program_id}`} className="font-medium text-slate-800 hover:text-emerald-700">
                        {it.program_name}
                      </Link>
                    </td>
                    <td className="px-3 py-3 text-slate-700">
                      <div className="flex items-center gap-1.5">
                        {it.label || <span className="text-slate-400 italic">Untitled</span>}
                        {it.has_password && <Lock className="w-3 h-3 text-slate-400" />}
                      </div>
                      {it.expires_at && (
                        <p className="text-[11px] text-amber-700 mt-0.5">expires {new Date(it.expires_at).toLocaleDateString()}</p>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${status.cls}`}>{status.label}</span>
                    </td>
                    <td className="px-3 py-3 text-right font-mono text-slate-700"><Eye className="w-3 h-3 inline-block mr-1 text-slate-400" />{it.views || 0}</td>
                    <td className="px-3 py-3 text-right font-mono text-emerald-700">{it.unique_visitors || 0}</td>
                    <td className="px-3 py-3 text-right font-mono text-slate-700"><FileDown className="w-3 h-3 inline-block mr-1 text-slate-400" />{it.pdf_downloads || 0}</td>
                    <td className="px-3 py-3 text-slate-500 text-xs">
                      {it.last_opened_at ? (
                        <span className="flex items-center gap-1"><ClockIcon className="w-3 h-3" /> {relativeTime(it.last_opened_at)}</span>
                      ) : <span className="text-slate-300">never</span>}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="inline-flex gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copy(it.token)}
                          disabled={it.status !== "active"}
                          data-testid={`share-copy-${it.token}`}
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => revoke(it.program_id, it.token, it.label)}
                          className="text-rose-600 hover:text-rose-700 border-rose-200"
                          data-testid={`share-revoke-${it.token}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}



// ============================================================
// Page

// ============================================================
// Donors tab — workspace-wide donor portals (Phase 3.39)
// ============================================================
function DonorsTab() {
  const [portals, setPortals] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null); // portal object or null

  const refresh = async () => {
    setLoading(true);
    try {
      const [pRes, progRes] = await Promise.all([
        api.get("/donor-portals"),
        api.get("/programs"),
      ]);
      setPortals(pRes.data.items || []);
      setPrograms(progRes.data || []);
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't load donors"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const copy = async (token) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/donor/${token}`);
      toast.success("Portal link copied");
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  const revoke = async (id, name) => {
    if (!window.confirm(`Revoke ${name}'s portal? They will lose access immediately and this cannot be undone.`)) return;
    try {
      await api.delete(`/donor-portals/${id}`);
      toast.success("Portal revoked");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't revoke"));
    }
  };

  return (
    <div data-testid="donors-tab">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
            <Heart className="w-5 h-5 text-emerald-600" /> Donor portals
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-xl">
            One tokenized URL per donor → branded landing showing every program you've granted them. Click-to-verify works everywhere. Revoke any time.
          </p>
        </div>
        <Button
          onClick={() => { setEditing(null); setShowCreate(true); }}
          className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-medium"
          data-testid="new-donor-button"
        >
          <Plus className="w-4 h-4 mr-1.5" /> New donor
        </Button>
      </header>

      {loading ? (
        <p className="text-sm text-slate-400 py-6">Loading…</p>
      ) : portals.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-slate-200 rounded-md" data-testid="donors-empty">
          <Heart className="w-8 h-8 mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">No donor portals yet.</p>
          <p className="text-xs text-slate-400 mt-1">Create one to give a funder a single branded URL across multiple programs.</p>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-2 sm:mx-0">
          <table className="w-full text-sm" data-testid="donors-table">
            <thead>
              <tr className="text-left border-b border-slate-200">
                <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wider text-slate-500">Donor</th>
                <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wider text-slate-500">Programs</th>
                <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wider text-slate-500 text-right">Views</th>
                <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wider text-slate-500 text-right">Unique</th>
                <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wider text-slate-500">Last opened</th>
                <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wider text-slate-500 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {portals.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`donor-row-${p.id}`}>
                  <td className="px-3 py-3">
                    <p className="font-medium text-slate-800">{p.donor_name}</p>
                    {p.donor_email && <p className="text-[11px] text-slate-400 mt-0.5">{p.donor_email}</p>}
                  </td>
                  <td className="px-3 py-3 text-slate-700">
                    <div className="flex flex-wrap gap-1">
                      {(p.programs || []).map((prog) => (
                        <span key={prog.id} className="text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded-sm">
                          {prog.name}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right font-mono text-slate-700"><Eye className="w-3 h-3 inline-block mr-1 text-slate-400" />{p.views || 0}</td>
                  <td className="px-3 py-3 text-right font-mono text-emerald-700">{p.unique_visitors || 0}</td>
                  <td className="px-3 py-3 text-slate-500 text-xs">
                    {p.last_opened_at ? (
                      <span className="flex items-center gap-1"><ClockIcon className="w-3 h-3" /> {relativeTime(p.last_opened_at)}</span>
                    ) : <span className="text-slate-300">never</span>}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <div className="inline-flex gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => copy(p.token)} data-testid={`donor-copy-${p.id}`} title="Copy portal URL">
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" asChild data-testid={`donor-preview-${p.id}`} title="Preview as donor">
                        <a href={`/donor/${p.token}`} target="_blank" rel="noreferrer">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setEditing(p); setShowCreate(true); }} data-testid={`donor-edit-${p.id}`} title="Edit donor">
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => revoke(p.id, p.donor_name)} className="text-rose-600 hover:text-rose-700 border-rose-200" data-testid={`donor-revoke-${p.id}`} title="Revoke portal">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <DonorEditor
          editing={editing}
          programs={programs}
          onClose={() => { setShowCreate(false); setEditing(null); }}
          onSaved={async () => { setShowCreate(false); setEditing(null); await refresh(); }}
        />
      )}
    </div>
  );
}

function DonorEditor({ editing, programs, onClose, onSaved }) {
  const [donorName, setDonorName] = useState(editing?.donor_name || "");
  const [donorEmail, setDonorEmail] = useState(editing?.donor_email || "");
  const [welcomeNote, setWelcomeNote] = useState(editing?.welcome_note || "");
  const [programIds, setProgramIds] = useState(
    new Set((editing?.programs || []).map((p) => p.id))
  );
  const [busy, setBusy] = useState(false);

  const toggle = (pid) => {
    const next = new Set(programIds);
    if (next.has(pid)) next.delete(pid); else next.add(pid);
    setProgramIds(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!donorName.trim()) return toast.error("Donor name is required");
    if (programIds.size === 0) return toast.error("Pick at least one program");
    setBusy(true);
    try {
      const payload = {
        donor_name: donorName.trim(),
        donor_email: donorEmail.trim() || null,
        welcome_note: welcomeNote.trim() || null,
        program_ids: Array.from(programIds),
      };
      if (editing) {
        await api.patch(`/donor-portals/${editing.id}`, payload);
        toast.success("Donor updated");
      } else {
        await api.post("/donor-portals", payload);
        toast.success("Donor portal created");
      }
      await onSaved();
    } catch (err) {
      toast.error(formatApiError(err, "Save failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 grid place-items-center p-4" onClick={onClose} data-testid="donor-editor-overlay">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={submit} className="p-6">
          <div className="flex items-start justify-between mb-5">
            <div>
              <h3 className="font-heading text-lg font-semibold">{editing ? "Edit donor" : "New donor"}</h3>
              <p className="text-xs text-slate-500 mt-1">Generates a tokenized URL — share it with the donor, revoke any time.</p>
            </div>
            <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="donor-editor-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-xs">Donor name *</Label>
              <Input
                value={donorName}
                onChange={(e) => setDonorName(e.target.value)}
                placeholder="e.g. Acme Foundation"
                required
                className="mt-1.5"
                data-testid="donor-name-input"
              />
            </div>
            <div>
              <Label className="text-xs">Donor email (optional)</Label>
              <Input
                type="email"
                value={donorEmail}
                onChange={(e) => setDonorEmail(e.target.value)}
                placeholder="contact@acme.org"
                className="mt-1.5"
                data-testid="donor-email-input"
              />
            </div>
            <div>
              <Label className="text-xs">Welcome note (optional)</Label>
              <Textarea
                value={welcomeNote}
                onChange={(e) => setWelcomeNote(e.target.value)}
                placeholder="Hi Acme team — here's our Q3 progress…"
                rows={3}
                className="mt-1.5 text-sm"
                data-testid="donor-welcome-note-input"
              />
              <p className="text-[11px] text-slate-400 mt-1">Shown above the program cards on the portal landing.</p>
            </div>
            <div>
              <Label className="text-xs">Programs *</Label>
              <p className="text-[11px] text-slate-400 mb-2">Pick which programs this donor sees. They will only access the ones you tick.</p>
              <div className="space-y-1 border border-slate-200 rounded-sm p-2 max-h-48 overflow-y-auto" data-testid="donor-program-picker">
                {programs.length === 0 ? (
                  <p className="text-xs text-slate-400 py-2 text-center">You don't have any programs yet.</p>
                ) : programs.map((p) => (
                  <label key={p.id} className="flex items-start gap-2 p-2 hover:bg-slate-50 rounded-sm cursor-pointer text-sm" data-testid={`donor-program-${p.id}`}>
                    <input
                      type="checkbox"
                      checked={programIds.has(p.id)}
                      onChange={() => toggle(p.id)}
                      className="mt-0.5 accent-emerald-500"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{p.name}</p>
                      {p.description && <p className="text-[11px] text-slate-400 line-clamp-1">{p.description}</p>}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button
              type="submit"
              disabled={busy}
              className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-medium"
              data-testid="donor-editor-save"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              {editing ? "Save changes" : "Create portal"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ============================================================
export default function Settings() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tabKey = params.get("tab") || "profile";
  const [data, setData] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/users/me/settings").then(({ data: d }) => {
      if (!alive) return;
      setData(d);
      setIndustries(d.industries || []);
    }).catch(() => alive && navigate("/login", { replace: true }))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [navigate]);

  const onSaved = (fresh) => {
    setData((d) => ({ ...d, ...fresh }));
  };

  if (loading || !data) {
    return <div className="min-h-screen grid place-items-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/array-logo.png" alt="Array Engine" className="h-7 w-auto" />
          </Link>
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="settings-back">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="mb-8">
          <p className="label-overline">Account</p>
          <h1 className="font-heading text-3xl tracking-tight font-semibold text-slate-900 mt-2">Settings</h1>
          <p className="text-slate-500 mt-1">Manage your profile, organization, team, and account preferences.</p>
        </div>

        <Tabs value={tabKey} onValueChange={(v) => setParams({ tab: v })}>
          <TabsList className="bg-white border border-slate-200 p-1 grid grid-cols-2 sm:grid-cols-6 w-full sm:w-auto" data-testid="settings-tabs">
            <TabsTrigger value="profile" data-testid="settings-tab-profile" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              <User className="w-4 h-4 mr-1.5" /> Profile
            </TabsTrigger>
            <TabsTrigger value="organization" data-testid="settings-tab-organization" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              <Building2 className="w-4 h-4 mr-1.5" /> Organization
            </TabsTrigger>
            <TabsTrigger value="team" data-testid="settings-tab-team" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-1.5" /> Team
            </TabsTrigger>
            <TabsTrigger value="donors" data-testid="settings-tab-donors" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              <Heart className="w-4 h-4 mr-1.5" /> Donors
            </TabsTrigger>
            <TabsTrigger value="share-links" data-testid="settings-tab-share-links" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              <Share2 className="w-4 h-4 mr-1.5" /> Share links
            </TabsTrigger>
            <TabsTrigger value="account" data-testid="settings-tab-account" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              <Lock className="w-4 h-4 mr-1.5" /> Account
            </TabsTrigger>
          </TabsList>

          <div className="bg-white border border-slate-200 rounded-md p-6 mt-5">
            <TabsContent value="profile" className="mt-0">
              <ProfileTab data={data} onSaved={onSaved} />
            </TabsContent>
            <TabsContent value="organization" className="mt-0">
              <OrganizationTab data={data} industries={industries} onSaved={onSaved} />
            </TabsContent>
            <TabsContent value="team" className="mt-0">
              <TeamTab />
            </TabsContent>
            <TabsContent value="donors" className="mt-0">
              <DonorsTab />
            </TabsContent>
            <TabsContent value="share-links" className="mt-0">
              <ShareLinksTab />
            </TabsContent>
            <TabsContent value="account" className="mt-0">
              <AccountTab data={data} />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
